import { commonMessages } from "@/messages/common";

type ApiErrorPayload = {
  message?: unknown;
  error?: unknown;
  detail?: unknown;
  attempts_remaining?: unknown;
};

export type ParsedApiError = {
  message: string;
  errorCode: string | null;
  field: string | null;
  attemptsRemaining: number | null;
};

function readAttemptsRemaining(value: unknown): number | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value < 0) {
    return null;
  }
  return value;
}

function readErrorCode(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readField(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function detailsFromDetailObject(detail: unknown): {
  message: string | null;
  errorCode: string | null;
  field: string | null;
} {
  if (!detail || typeof detail !== "object") {
    return { message: null, errorCode: null, field: null };
  }

  const nested = detail as {
    message?: unknown;
    error?: unknown;
    field?: unknown;
  };

  return {
    message: messageFromDetail(detail),
    errorCode: readErrorCode(nested.error),
    field: readField(nested.field),
  };
}

/** Extracts message, error code, and attempts remaining from an API error body. */
export function parseApiErrorDetails(
  data: unknown,
  fallback: string = commonMessages.api.genericError,
): ParsedApiError {
  const message = parseApiErrorBody(data, fallback);

  if (!data || typeof data !== "object") {
    return { message, errorCode: null, field: null, attemptsRemaining: null };
  }

  const parsed = data as ApiErrorPayload;
  const fromDetail = detailsFromDetailObject(parsed.detail);

  return {
    message,
    errorCode: readErrorCode(parsed.error) ?? fromDetail.errorCode,
    field: fromDetail.field,
    attemptsRemaining: readAttemptsRemaining(parsed.attempts_remaining),
  };
}

function messageFromDetail(detail: unknown): string | null {
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }

  if (detail && typeof detail === "object") {
    const nested = detail as { message?: unknown; error?: unknown };
    if (typeof nested.message === "string" && nested.message.trim()) {
      return nested.message.trim();
    }
    if (typeof nested.error === "string" && nested.error.trim()) {
      return nested.error.trim();
    }
  }

  return null;
}

/** Extracts a user-facing message from a parsed API error body. */
export function parseApiErrorBody(
  data: unknown,
  fallback: string = commonMessages.api.genericError,
): string {
  if (typeof data === "string") {
    return parseApiErrorMessage(data, fallback);
  }

  if (!data || typeof data !== "object") {
    return fallback;
  }

  const parsed = data as ApiErrorPayload;

  if (typeof parsed.message === "string" && parsed.message.trim()) {
    return parsed.message.trim();
  }

  if (typeof parsed.error === "string" && parsed.error.trim()) {
    return parsed.error.trim();
  }

  const fromDetail = messageFromDetail(parsed.detail);
  if (fromDetail) {
    return fromDetail;
  }

  return fallback;
}

/** Extracts a user-facing message from API JSON error bodies. */
export function parseApiErrorMessage(
  raw: string,
  fallback: string = commonMessages.api.genericError,
): string {
  const trimmed = raw.trim();
  if (!trimmed) {
    return fallback;
  }

  try {
    return parseApiErrorBody(JSON.parse(trimmed) as unknown, fallback);
  } catch {
    /* plain text body */
  }

  return trimmed.length > 280 ? `${trimmed.slice(0, 277)}…` : trimmed;
}
